import { useRef, useState, useCallback } from "react";
import api from "../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";

interface OrderData {
  Orderid: number | string;
  [key: string]: any;
}

interface UseExportProps {
  setLoading: (value: boolean) => void;
}

export interface UseExportReturn {
  inputRef: React.RefObject<HTMLInputElement>;
  handleExport: (
    type: string,
    data: OrderData[],
    selectedOption?: string,
    searchTerm?: string,
    page?: number,
    rowsPerPage?: number,
    getDateRange?: (filterOption?: string) => { from: string; to: string },
    selectedDate?: string,
    selectedDate2?: string
  ) => Promise<void>;
  totalRecords: number;
}

const useExport = ({ setLoading }: UseExportProps): UseExportReturn => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const handleExport = useCallback(
    async (
      type: string,
      data: OrderData[],
      selectedOption: string = "Today",
      searchTerm: string = "",
      page: number = 0,
      rowsPerPage: number = 10,
      getDateRange?: (filterOption?: string) => { from: string; to: string },
      selectedDate?: string,
      selectedDate2?: string
    ): Promise<void> => {
      if (!data || data.length === 0) {
        notifyWarning("No records found to export");
        return;
      }

      setLoading(true);

      try {
        let from = "";
        let to = "";
        if (selectedOption === "CustomizeDate" && selectedDate && selectedDate2) {
          from = selectedDate;
          to = selectedDate2;
        } else if (getDateRange) {
          const range = getDateRange(selectedOption);
          from = range.from;
          to = range.to;
        }

        // 🧾 Construct query params
        const queryParams = new URLSearchParams({
          searchBy: searchTerm || "",
          billNo: searchTerm || "",
          pageNumber: String(page + 1),
          pageSize: String(rowsPerPage),
          dateFilter: selectedOption === "CustomizeDate" ? "custom" : selectedOption?.toLowerCase() || "today",
        }).toString();

        // 🔗 Build API endpoint dynamically
        let endpoint = "";
        if (type === "CSV") {
          endpoint = `/app/v2/import_export?export=csv&${queryParams}`;
        } else if (type === "PDF") {
          endpoint = `/app/v2/import_export?export=pdf&${queryParams}`;
        } else {
          notifyError("Invalid export format");
          return;
        }

        const response = await api.get(endpoint);

        if (response?.data?.totalRecords !== undefined) {
          setTotalRecords(response.data.totalRecords);
        }
        if (response.status === 200 || response.status === 201) {
          notifySuccess(`${type} export email sent successfully`);
        } else {
          notifyError(`Failed to export ${type} data`);
        }
      } catch (error) {
        console.error("Export error:", error);
        notifyError(`Error exporting ${type}. Please try again.`);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  return { inputRef, handleExport, totalRecords };
};

export default useExport;
